import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity, HeartPulse, Pill
} from 'lucide-react'
import UsersRoundCollaboration from '../../../components/ERP/icons/UsersRoundCollaboration'
import KPICard from '../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import { CalendarDatePicker } from '../../../components/ERP/Calendar/Calendar'
import OnboardingTour from '../../../components/ERP/Onboarding/OnboardingTour'
import { patientService } from '../../../services/patientApi'
import { useAuth } from '../../../contexts/AuthContext'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { DASHBOARD_ONBOARDING_STEPS, DASHBOARD_TOUR_ID } from './dashboardOnboarding'
import ShiftGlanceSheet from './ShiftGlanceSheet'
import AlteredSignsSheet from './AlteredSignsSheet'
import FeverCurve from '../../../components/ERP/FeverCurve/FeverCurve'
import OximetryChart from '../../../components/ERP/OximetryChart/OximetryChart'
import PressureChart from '../../../components/ERP/PressureChart/PressureChart'
import GlucoseChart from '../../../components/ERP/GlucoseChart/GlucoseChart'
import HeartRateChart from '../../../components/ERP/HeartRateChart/HeartRateChart'
import PatientFloatFilter from '../../../components/ERP/PatientFloatFilter/PatientFloatFilter'
import '../../../components/ERP/VitalPlot/VitalPlot.css'
import ClinicalSpotlight, { ClinicalPatientStage } from '../../../components/ERP/ClinicalSpotlight/ClinicalSpotlight'
import DashboardBoard from './DashboardBoard'
import WaterDailyGauge from '../../../components/ERP/Hydration/WaterDailyGauge'
import HydrationWeekChart from '../../../components/ERP/Hydration/HydrationWeekChart'
import { PatientsKpiSkeleton } from './PatientsSkeletons'
import '../Financeiro/Dashboard/FinanceDashboardPage.css'
import './PatientsPage.css'

function todayKey() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date())
}

function PatientsDashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasPermission } = usePermissions()
  const canOpenChart = hasPermission(MODULES.PATIENTS, ACTIONS.VIEW)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [shiftsOpen, setShiftsOpen] = useState(false)
  const [alteredOpen, setAlteredOpen] = useState(false)
  const [feverId, setFeverId] = useState(null)
  const [feverPoints, setFeverPoints] = useState([])
  const [feed, setFeed] = useState([])
  const [feedHasMore, setFeedHasMore] = useState(false)
  const [feedBefore, setFeedBefore] = useState(null)
  const [feedDay, setFeedDay] = useState('')
  const [feedLoading, setFeedLoading] = useState(false)
  const [tourReplay, setTourReplay] = useState(0)
  const feverIdRef = useRef(null)
  const feedDayRef = useRef('')
  const feedRequestRef = useRef(0)
  const feedLockRef = useRef(false)
  feverIdRef.current = feverId
  feedDayRef.current = feedDay

  useEffect(() => {
    let cancelled = false
    const load = (patientId) => {
      setLoading(true)
      patientService.dashboard(patientId ? { patientId } : {})
        .then((response) => {
          if (cancelled) return
          const payload = response.data || null
          setData(payload)
          setFeverId(payload?.temperature?.patientId || null)
          setFeverPoints(payload?.temperature?.points || [])
          if (!feedDayRef.current) {
            setFeed(payload?.activity || [])
            setFeedHasMore(Boolean(payload?.activityHasMore))
            setFeedBefore(payload?.activityNextBefore || null)
          }
          setError('')
        })
        .catch((err) => {
          if (!cancelled) setError(err.response?.data?.message || 'Não foi possível abrir o painel')
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }
    load()
    const refresh = () => load(feverIdRef.current)
    window.addEventListener('wfmed-shift-updated', refresh)
    window.addEventListener('wfmed-clinical-updated', refresh)
    window.addEventListener('focus', refresh)
    return () => {
      cancelled = true
      window.removeEventListener('wfmed-shift-updated', refresh)
      window.removeEventListener('wfmed-clinical-updated', refresh)
      window.removeEventListener('focus', refresh)
    }
  }, [])

  const changeFeverPatient = (patientId) => {
    setFeverId(patientId)
    patientService.temperatureSeries(patientId)
      .then((response) => setFeverPoints(response.data?.points || []))
      .catch(() => setFeverPoints([]))
  }

  const applyFeedPage = (page, append) => {
    const items = page?.items || []
    setFeed((current) => {
      if (!append) return items
      const seen = new Set(current.map((item) => item.id))
      return [...current, ...items.filter((item) => !seen.has(item.id))]
    })
    setFeedHasMore(Boolean(page?.hasMore))
    setFeedBefore(page?.nextBefore || null)
  }

  const loadFeed = useCallback((params, append) => {
    const requestId = feedRequestRef.current + 1
    feedRequestRef.current = requestId
    feedLockRef.current = true
    setFeedLoading(true)
    patientService.dashboardActivity(params)
      .then((response) => {
        if (feedRequestRef.current !== requestId) return
        applyFeedPage(response.data || {}, append)
      })
      .catch(() => {
        if (feedRequestRef.current === requestId && !append) {
          setFeed([])
          setFeedHasMore(false)
          setFeedBefore(null)
        }
      })
      .finally(() => {
        if (feedRequestRef.current === requestId) {
          feedLockRef.current = false
          setFeedLoading(false)
        }
      })
  }, [])

  const loadOlder = useCallback(() => {
    if (!feedHasMore || feedLockRef.current || !feedBefore) return
    loadFeed({
      before: feedBefore,
      day: feedDay || undefined,
      limit: 8
    }, true)
  }, [feedBefore, feedDay, feedHasMore, loadFeed])

  const changeFeedDay = (day) => {
    const next = day || ''
    setFeedDay(next)
    feedDayRef.current = next
    if (!next) {
      setFeed(data?.activity || [])
      setFeedHasMore(Boolean(data?.activityHasMore))
      setFeedBefore(data?.activityNextBefore || null)
      return
    }
    loadFeed({ day: next, limit: 8 }, false)
  }

  const summary = data?.summary || {}
  const feverPatients = (data?.roster || []).map((patient) => ({
    id: patient.id,
    name: patient.name,
    photoUrl: patient.photo_url,
    detail: [patient.bed || 'Sem leito', patient.temperature != null ? `${Number(patient.temperature).toFixed(1)}°C` : null].filter(Boolean).join(' · ')
  }))
  const kpis = [
    { label: 'Plantões abertos', value: String(summary.openShifts || 0), change: `${summary.patientsOnShift || 0} pacientes com equipe`, up: true, icon: UsersRoundCollaboration, color: 'text-emerald-500', bg: 'bg-emerald-50', onClick: () => setShiftsOpen(true), tourAnchor: 'dashboard-shifts' },
    { label: 'Sinais alterados', value: String(summary.altered || 0), change: `${summary.internado || 0} internados`, up: false, icon: HeartPulse, color: 'text-rose-500', bg: 'bg-rose-50', onClick: () => setAlteredOpen(true), tourAnchor: 'dashboard-altered' },
    { label: 'Evoluções hoje', value: String(summary.evolutionsToday || 0), change: `${summary.active || 0} pacientes ativos`, up: true, icon: Activity, color: 'text-blue-500', bg: 'bg-blue-50' },
    { label: 'Prescrições ativas', value: String(summary.activePrescriptions || 0), change: 'em vigência hoje', up: true, icon: Pill, color: 'text-amber-500', bg: 'bg-amber-50' }
  ]

  return (
    <div className="finance-dashboard patients-module" aria-busy={loading}>
      <div className="finance-dashboard-header">
        <div>
          <h1 className="finance-dashboard-title">
            Painel <span className="finance-dashboard-title-highlight">clínico</span>
          </h1>
          <p className="finance-dashboard-subtitle">
            Plantão, sinais e lançamentos do dia
          </p>
        </div>
        <div className="patients-dashboard-tools">
          <div data-onboarding="dashboard-calendar">
            <CalendarDatePicker
              className={`patients-dashboard-calendar${feedDay ? ' is-set' : ''}`}
              value={feedDay}
              maxDate={todayKey()}
              placeholder="Hoje"
              title="Até este dia"
              subtitle="O feed mostra o que aconteceu até aqui"
              onChange={changeFeedDay}
            />
          </div>
          {feedDay && (
            <button type="button" className="patients-dashboard-clear" aria-label="Voltar para agora" title="Voltar para agora" onClick={() => changeFeedDay('')}>
              ×
            </button>
          )}
          <div data-dashboard-layout-tools />
          <button type="button" className="patients-btn patients-btn--ghost patients-btn--compact patients-tour-help" onClick={() => setTourReplay((current) => current + 1)}>
            Como usar
          </button>
        </div>
      </div>

      {error && (
        <div className="finance-dashboard-error-banner" role="alert">
          <p>{error}</p>
        </div>
      )}

      {loading && !data ? (
        <PatientsKpiSkeleton />
      ) : (
        <div className="finance-dashboard-kpi-grid" data-onboarding="dashboard-kpis">
          {kpis.map((kpi) => <KPICard key={kpi.label} {...kpi} />)}
        </div>
      )}

      <DashboardBoard
        userId={user?.id}
        items={[
          data?.hydration && {
            id: 'gauge',
            anchor: 'dashboard-hydration',
            content: <WaterDailyGauge liters={data.hydration.liters} percent={data.hydration.percent} />
          },
          data?.hydration && {
            id: 'week',
            content: <HydrationWeekChart days={data.hydration.days} />
          },
          {
            id: 'feed',
            content: (
              <ClinicalSpotlight
                feedOnly
                activity={feed}
                hasMore={feedHasMore}
                loadingOlder={feedLoading}
                onLoadOlder={loadOlder}
                emptyLabel={feedDay ? 'Nenhum lançamento até esse dia.' : 'Nenhum lançamento ainda.'}
                onOpenChart={canOpenChart ? (id) => navigate(`/erp/pacientes/${id}`) : null}
              />
            )
          },
          {
            id: 'heart',
            anchor: 'dashboard-heart',
            content: <HeartRateChart points={feverPoints} />
          },
          {
            id: 'fever',
            anchor: 'dashboard-charts',
            content: <FeverCurve points={feverPoints} />
          },
          { id: 'oxygen', content: <OximetryChart points={feverPoints} /> },
          { id: 'pressure', content: <PressureChart points={feverPoints} /> },
          { id: 'glucose', content: <GlucoseChart points={feverPoints} /> }
        ].filter(Boolean)}
      />

      <PatientFloatFilter
        patients={feverPatients}
        value={feverId}
        onChange={changeFeverPatient}
      />

      <ClinicalPatientStage
        patients={data?.roster || []}
        selectedId={feverId}
        onSelectPatient={changeFeverPatient}
        onOpenChart={canOpenChart ? (id) => navigate(`/erp/pacientes/${id}`) : null}
      />

      <ShiftGlanceSheet open={shiftsOpen} onClose={() => setShiftsOpen(false)} />
      <AlteredSignsSheet open={alteredOpen} patients={data?.triage || []} onClose={() => setAlteredOpen(false)} />
      <OnboardingTour
        tourId={DASHBOARD_TOUR_ID}
        steps={DASHBOARD_ONBOARDING_STEPS}
        userId={user?.id}
        ready={!loading && Boolean(data)}
        replayToken={tourReplay}
      />
    </div>
  )
}

export default PatientsDashboardPage
